import React, { useState, useRef, useImperativeHandle } from 'react'
import { TouchableOpacity, View } from 'react-native'
import Video, {
  VideoRef,
  ResizeMode,
  OnLoadData,
  OnBufferData,
  ReactVideoProps,
} from 'react-native-video'
import { StyleService, useStyleSheet } from '@src/style/service'
import { ContentLoader, Rect } from '@src/components/ContentLoader'
import { LoadingIndicator } from '@src/components/LoadingIndicator'
import { IconButton } from '../base'
import { useFitVideoInView } from './hooks'

export interface VideoPlayerProps extends ReactVideoProps {
  source: any
  onLoad?: (data: OnLoadData) => void
  onPlay?: () => void
  onPause?: () => void
  onError?: (error: any) => void
  style?: any
  showControls?: boolean
  playOnLoad?: boolean
}

export const VideoPlayer = React.forwardRef<VideoRef | null, VideoPlayerProps>(
  (props, forwardedRef) => {
    const {
      source,
      onLoad = () => 0,
      onPlay = () => 0,
      onPause = () => 0,
      onError = () => 0,
      style: videoStyle,
      showControls = true,
      playOnLoad = false,
      ...restProps
    } = props

    const styles = useStyleSheet(themedStyles)
    const [isReadyForDisplay, setIsReadyForDisplay] = useState(false)
    const videoRef = useRef<VideoRef | null>(null)
    const [isLoaded, setIsLoaded] = useState(false)
    const [isPlaying, setIsPlaying] = useState(false)
    const [isBuffering, setIsBuffering] = useState(false)
    const { setViewDimensions, setVideoDimensions, dimensions } = useFitVideoInView()

    useImperativeHandle(forwardedRef, () => videoRef.current as VideoRef, [])

    const isReady = isReadyForDisplay && isLoaded
    const showBufferingIndicator = isReady && isBuffering && !isPlaying

    const handleLoad = (data: OnLoadData) => {
      setVideoDimensions({ width: data.naturalSize.width, height: data.naturalSize.height })
      setIsReadyForDisplay(true)
      setIsLoaded(true)
      if (playOnLoad) {
        setIsPlaying(true)
      }
      onLoad(data)
    }

    const handleBuffer = (data: OnBufferData) => {
      setIsBuffering(data.isBuffering)
    }

    const handlePlaybackStateChanged = (data: { isPlaying: boolean }) => {
      const wasPlaying = isPlaying
      setIsPlaying(data.isPlaying)

      if (data.isPlaying && !wasPlaying) {
        onPlay()
      } else if (!data.isPlaying && wasPlaying && !isBuffering) {
        onPause()
      }
    }

    const showCustomPlayButton = isReady && !isBuffering && !isPlaying

    return (
      <View style={styles.container} onLayout={(e) => setViewDimensions(e.nativeEvent.layout)}>
        {!isReady && (
          <ContentLoader
            style={{ position: 'absolute', height: '100%', width: '100%', zIndex: 50 }}
          >
            <Rect x="0" y="0" width="100%" height="100%" rx="6" ry="6" />
          </ContentLoader>
        )}
        <View style={styles.videoPlayer}>
          {showControls && (
            <View style={styles.videoOverlay} pointerEvents="box-none">
              {showCustomPlayButton && (
                <IconButton
                  accessibilityLabel="Play"
                  iconName="play"
                  iconProps={{ weight: 'fill' }}
                  onPress={() => videoRef.current?.resume()}
                  type="primary"
                  size="l"
                />
              )}
              {showBufferingIndicator && (
                <TouchableOpacity
                  style={[styles.overlayButton, styles.bufferingButton]}
                  onPress={() => setIsPlaying(false)}
                  accessibilityLabel="Pause"
                >
                  <LoadingIndicator
                    size="large"
                    viewStyles={styles.bufferIndicatorIcon}
                    color="black"
                  />
                </TouchableOpacity>
              )}
            </View>
          )}
          <Video
            ref={videoRef}
            source={source}
            style={[styles.video, dimensions, videoStyle]}
            onLoad={handleLoad}
            onBuffer={handleBuffer}
            onPlaybackStateChanged={handlePlaybackStateChanged}
            onError={onError}
            resizeMode={ResizeMode.COVER}
            controls={showControls}
            paused={!isPlaying}
            {...restProps}
          />
        </View>
      </View>
    )
  },
)

const themedStyles = StyleService.create({
  container: {
    flex: 1,
    alignItems: 'center',
  },
  videoPlayer: {
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  video: {
    borderRadius: 8,
  },
  videoOverlay: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  overlayButton: {
    alignSelf: 'center',
    borderRadius: 90,
    justifyContent: 'center',
    alignItems: 'center',
    width: 56,
    height: 56,
  },
  bufferingButton: {
    backgroundColor: 'theme.solid.white',
  },
  bufferIndicatorIcon: {
    paddingLeft: 3,
    paddingTop: 3,
  },
  controlIcon: {
    marginRight: 8,
    color: 'theme.solid.white',
  },
})
